import { LocalizedLabel } from 'inside-fifa/pages/generic-tables/configurations/partials/filters/filter/dto';

export type GenericTableDataFileUploadDto = {
    file: File;
};

export type GenericTableDataFileDto = {
    fileId: number;
    name: string;
    uploadDateTimestamp: string;
    modifiedTimestamp: string;
    sheets: GenericTableSheetDto[];
    deleteFile: number;
    azureBlobPath: string;
};

export type GenericTableDataFileUploadResponseDto = Pick<
    GenericTableDataFileDto,
    | 'fileId' //
    | 'name'
    | 'uploadDateTimestamp'
    | 'modifiedTimestamp'
>;

export type GenericResponseFileDto = {
    status?: number;
    message?: string;
    data?: string;
};

export type GenericFileDownloadDto = {
    file?: Blob;
    url?: string;
    a?: HTMLAnchorElement;
    status?: number;
    error?: string;
};
export type GenericTableSheetDto = {
    sheetId: number;
    name: string;
};

export type DataSourceDto = {
    fileId: number | null;
    sheetId: number;
};

export type GenericTableResponseConfigDto = {
    status?: number;
    message?: string;
    data?: string;
};

export type UniqueColumnValuesResponse = {
    values: string[];
};

export type GenericTableConfigCreationDto = Pick<GenericTableConfigDto, 'name' | 'dataSource'>;

export type GenericTableConfigPatchDto =
    | {
          name?: string;
          config?: GenericTableConfigDetailsDto;
          modifiedTimestamp: string;
      }
    | {
          dataSource: DataSourceDto;
          force?: boolean;
          modifiedTimestamp: string;
      };

export type GenericTableConfigDto = {
    id: number;
    name: string;
    dataSource: DataSourceDto;
    config: GenericTableConfigDetailsDto;
    createdTimestamp: string;
    modifiedTimestamp: string;
};

export type DefaultColumnOrderDto = {
    key: string;
    orderType: OrderType;
};

export type KeyAttributeValue = {
    color: string;
    icon: string;
};

export type KeyAttributeMapping = {
    key: string;
    color: string;
    icon: string;
};

export type GenericTableConfigDetailsDto = {
    columns: GenericTableColumn[];
    filters: FiltersConfig;
    itemsPerPage: number;
    paginationType: PaginationType;
    defaultColumnOrder: DefaultColumnOrderDto;
    columnTitle?: string;
    keyAttributeColumn?: string;
    keyAttributeColumnValues?: KeyAttributeMapping[];
    viewMode?: ViewMode;
    filterMode?: FilterMode;
};

export enum PaginationType {
    Unknown = 0,
    Pagination = 1,
    ShowMoreLess = 2,
}

export enum OrderType {
    Unknown = 0,
    Asc = 1,
    Desc = 2,
}

export type FiltersConfig = {
    isSearchBoxEnabled?: boolean;
    customFilters: CustomFilter[];
};

export type CustomFilter = {
    type: GenericTableFilterType;
    dataColumn?: string;
    view?: GenericTableFilterView;
    format?: GenericTableDateFilterFormat;
    shouldShowOutsidePanel: boolean;
    label: LocalizedLabel;
    allOptionsLabel?: LocalizedLabel;
    hideOptionAll: boolean;
};

export enum GenericTableFilterView {
    Unknown = 0,
    Default,
    ButtonGroup,
}

export enum GenericTableFilterType {
    Unknown = 0,
    SingleSelectFilter,
    MultipleSelectFilter,
    DateFilter,
}

export enum GenericTableDateFilterFormat {
    Unknown = 0,
    YearOnly,
    WithoutYear,
    FullDate,
}

export type TranslatableValue = Record<string, string>;

export interface GenericTableColumnBase {
    type: GenericTableColumnType;
    key: string;
    title: string;
    isSortingEnabled: boolean;
    isHidden: boolean;
    isHideOnMobile: boolean;
    shouldShowInExpandableRow: boolean;
    showAsFullWidthRow?: boolean;
    columnTitle: TranslatableValue | null;
    customTemplateString: string | null;
}

// We've planned to have different (involving discrimination by type) configurable fields per column type,
// however there are some issues on the backend side with serialisation/deserialisation
// that forced us to implement it as "one shape to fit them all" object.
// See the following PR on backend for some more context of the problem:
// https://dev.azure.com/FIFA-Commercial/CxM%20-%20Headless%20CMS/_git/FIFA.MEDIAHUB/pullrequest/29167?path=/InsideFifa/InsideFifa.BackOffice.Management.Core/GenericTableConfiguration/Domain/Columns/GenericTableColumn.cs&version=GBdevelop&line=6&lineEnd=7&lineStartColumn=1&lineEndColumn=1&type=2&lineStyle=plain&_a=files&iteration=2&base=0
export interface GenericTableColumn extends GenericTableColumnBase {
    numberFormat: GenericTableNumberFormat;
    dateFormat: GenericTableDateColumnFormat;
    trueLabel: TranslatableValue | null;
    falseLabel: TranslatableValue | null;
    isLogoVisible?: boolean;
}

// Discriminated column types approach that may be the way when backend resolves
// the problem with serialisation/deserialisation
// export interface GenericTableBooleanColumn extends GenericTableColumnBase {
//     type: GenericTableColumnType.Boolean;
//     trueLabel: TranslatableValue | null;
//     falseLabel: TranslatableValue | null;
// }
//
// export interface GenericTableTextColumn extends GenericTableColumnBase {
//     type: GenericTableColumnType.Text;
// }
//
// export interface GenericTableNumberColumn extends GenericTableColumnBase {
//     type: GenericTableColumnType.Number;
//     format: GenericTableNumberFormat;
// }
//
// export interface GenericTableDateColumn extends GenericTableColumnBase {
//     type: GenericTableColumnType.Date;
//     format: GenericTableDateColumnFormat;
// }
//
// export type GenericTableColumn =
//     | GenericTableTextColumn
//     | GenericTableNumberColumn
//     | GenericTableBooleanColumn
//     | GenericTableDateColumn;

export enum GenericTableColumnType {
    Unknown = 0,
    Text = 1,
    Number = 2,
    Url = 3,
    Email = 4,
    Image = 5,
    FdcpConfederation = 6,
    FdcpAssociation = 7,
    Date = 8,
    Boolean = 9,
    BynderId = 10,
}

export enum GenericTableNumberFormat {
    Unknown = 0,
    Raw = 1,
    ShortNumber = 2,
    USD = 3,
    ShortUSD = 4,
    Percentage = 5,
}

export enum GenericTableDateColumnFormat {
    Unknown = 0,
    YearOnly = 1,
    WithoutYear = 2,
    FullDate = 3,
}

export enum ViewMode {
    Table = 0,
    Grid = 1,
}

export enum FilterMode {
    Top = 0,
    Sidebar = 1,
}
